import * as React from 'react'
import type { Roles } from '../api-types'

/**
 * Whether `roles` grants `role`.
 *
 * `whoami` sends roles as a map of identifier → granted flag, so a role counts as
 * held only when it is present AND `true`. A missing map (signed out, or whoami not
 * yet answered) degrades to `false` rather than throwing — callers can pass the raw
 * `session?.roles` without guarding it first.
 */
export const hasRole = (roles: Roles | null | undefined, role: string): boolean =>
  roles?.[role] === true

export interface RoleGateProps {
  /** The viewer's roles, straight from the shared session (`Session.roles`). */
  roles: Roles | null | undefined;
  /**
   * The role(s) that unlock `children`. A single identifier, or a list — holding
   * ANY one of the list is enough (logical OR), the common "manager OR admin" case.
   */
  require: string | string[];
  /** Rendered when the viewer holds (any of) the required role(s). */
  children: React.ReactNode;
  /** Rendered otherwise. Defaults to nothing, so a gated section simply disappears. */
  fallback?: React.ReactNode;
}

/**
 * Renders `children` only when the viewer holds (any of) the required role(s), and
 * `fallback` (nothing by default) otherwise — the reusable way to hang a section off
 * a role.
 *
 * Deliberately a thin, data-in wrapper: it does NOT read the session itself. The
 * caller passes `roles` from the shared session, so this stays pure (and trivially
 * testable / storybook-able) and the app shell remains the single reader of
 * `whoami`. This mirrors how the styleguide injects `createPersistedState` into
 * {@link createListToggle} rather than importing the shared singleton here.
 *
 * ```tsx
 * <RoleGate roles={session?.roles} require='Ttree.Medialib:Manager'>
 *   <TechnicalDetails session={session} />
 * </RoleGate>
 * ```
 */
export const RoleGate: React.FC<RoleGateProps> = ({ roles, require, children, fallback = null }) => {
  const required = Array.isArray(require) ? require : [require]
  const granted = required.some(role => hasRole(roles, role))
  return <>{granted ? children : fallback}</>
}
